/**
 * API Client
 * Axios-based client for backend API calls
 */

import axios, { AxiosInstance, AxiosError } from "axios";

declare global {
  interface Window {
    APP_ENV?: {
      VITE_API_BASE_URL?: string;
    };
  }
}

const getApiBaseUrl = (): string => {
  // Check if we're in production (window.APP_ENV is available)
  if (typeof window !== "undefined" && window.APP_ENV?.VITE_API_BASE_URL) {
    const baseUrl = window.APP_ENV.VITE_API_BASE_URL;
    // Ensure it ends with /api
    return baseUrl.endsWith("/api") ? baseUrl : `${baseUrl}/api`;
  }

  // Check build-time environment variable
  if (import.meta.env.VITE_API_BASE_URL) {
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    return baseUrl.endsWith("/api") ? baseUrl : `${baseUrl}/api`;
  }

  throw new Error("API base URL is not configured. Set VITE_API_BASE_URL environment variable or ensure window.APP_ENV.VITE_API_BASE_URL is available.");
};

const API_BASE_URL = getApiBaseUrl();
console.log("API_BASE_URL:", API_BASE_URL);

// Global notification handler
let globalShowNotification: ((type: 'success' | 'error' | 'warning' | 'info', message: string) => void) | null = null;

export const setGlobalNotificationHandler = (handler: (type: 'success' | 'error' | 'warning' | 'info', message: string) => void) => {
  globalShowNotification = handler;
};

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Enable cookies
  timeout: 30000, // 30 seconds
});

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Handle authentication errors
    if (error.response?.status === 401) {
      // Redirect to login if not authenticated
      const currentPath = window.location.pathname;
      if (currentPath !== "/login") {
        window.location.href = "/login";
      }
    }

    // Global error toast handling
    if (globalShowNotification && error.response) {
      const status = error.response.status;
      const message = getErrorMessage(error);

      if (status >= 500) {
        globalShowNotification('error', message);
      } else if (status >= 400 && status !== 401 && status !== 404) { // Skip 401s (auth) and 404s (expected "not found")
        globalShowNotification('warning', message);
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;

// Helper function to extract error message
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error || error.message || "An error occurred";
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "An unknown error occurred";
}

// Re-export service APIs for centralized importing
export { predictionsApi } from "./predictions.service";
export { tagsApi } from "./tags.service";
